// features/automation/components/RunDetailsPanel.js
 
  import React, { useEffect, useRef, useState, useCallback } from 'react';
  import ReactDOM from 'react-dom';
  import PropTypes from 'prop-types';
  import { permutationLabel } from '../utils/labels';
  import { CircleCheck, CircleX, MinusCircle, Ban, Layers, Clock, AlertTriangle, Image as ImageIcon, Monitor, Radio, Globe, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
  import { useStepCache, usePermCache } from '../../../store/automation/runStore';
  import { mergeSteps, mergeRecord, permKeyOf } from '../../../shared/utils/mergeRunFrames';
  import {
    PERMUTATION_STATUS,
    getPermutationStatus,
    resolveRunCounts,
  } from '../../../shared/utils/permutationStatus';
  import {
    PermutationSteps,
    STATUS_BADGE,
    StepTypeIcon,
    getPermBadge,
    getPermFailureText,
    getStepBadge,
    getStepState,
    normaliseStreamStepType,
  } from './runResults/StepDetail';
  import '../styles/RunProgressBar.css';

 
  const getScreenshotUrl = (step) =>
    step.streamScreenshot || step.screenshot || (step.description?.startsWith('data:image') ? step.description : null);
 
  /* ── Web-UI Steps View ─────────────────────────────────────────── */
 
  const WebUiStepsView = ({ permutations }) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const stepsRef = useRef(null);
 
    // permutations already have cached steps baked in by RunDetailsPanel
    const allSteps = permutations.flatMap((p) => p.steps || []);
 
    // Auto-select latest step with a screenshot as it streams in
    const latestScreenshotStep = [...allSteps].reverse().find(s => getScreenshotUrl(s));
 
    // The step whose screenshot to show — user selection takes priority, otherwise latest
    const activeStep = selectedStep && getScreenshotUrl(selectedStep) ? selectedStep : latestScreenshotStep;
    const activeScreenshot = activeStep ? getScreenshotUrl(activeStep) : null;
 
    const handleStepClick = useCallback((step) => {
      setSelectedStep(prev => prev === step ? null : step);
    }, []);
 
    return (
<>
<div className="mock-run-details__stream-layout">
          {/* Left: steps list */}
<div className="mock-run-details__stream-steps" ref={stepsRef}>
            {permutations.flatMap((p, pIdx) => {
              const permLabel = permutations.length > 1 ? (
<div
                  key={`perm-label-${pIdx}`}
                  className="mock-run-details__perm-label"
>
<Layers size={10} />
<span>{permutationLabel(p.index || pIdx + 1, p.description)}</span>
                  {getPermutationStatus(p) && (
<span className={`mock-run-details__status-badge ${getPermBadge(p).className}`}>
                      {React.createElement(getPermBadge(p).icon, { size: 9 })}
                      {getPermBadge(p).label}
</span>
                  )}
</div>
              ) : null;
 
              const stepRows = (p.steps || []).map((step, sIdx) => {
                const state = getStepState(step);
                const badge = getStepBadge(step);
                const BadgeIcon = badge.icon;
                const hasScreenshot = !!getScreenshotUrl(step);
                const isActive = activeStep === step;
                const errors = state === 'failed' && step.assertionResponse?.results?.length
                  ? step.assertionResponse.results
                  : null;

                // Group header — section divider, no timing or status badge
                if (state === 'header') {
                  return (
<div
                      key={`${pIdx}-${sIdx}`}
                      className="mock-run-details__step-row mock-run-details__step-row--header"
>
<div className="mock-run-details__step-left">
<div className="mock-run-details__step-header">
<StepTypeIcon stepType={step.stepType} size={12} />
<span className="mock-run-details__step-name">{step.name}</span>
</div>
</div>
</div>
                  );
                }

                return (
<div
                    key={`${pIdx}-${sIdx}`}
                    className={[
                      'mock-run-details__step-row',
                      state === 'failed' ? 'mock-run-details__step-row--failed' : '',
                      state === 'skipped' ? 'mock-run-details__step-row--skipped' : '',
                      state === 'disabled' ? 'mock-run-details__step-row--disabled' : '',
                      state === 'in_progress' ? 'mock-run-details__step-row--in-progress' : '',
                      isActive ? 'mock-run-details__step-row--active' : '',
                      hasScreenshot ? 'mock-run-details__step-row--clickable' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={hasScreenshot ? () => handleStepClick(step) : undefined}
>
<div className="mock-run-details__step-left">
<div className="mock-run-details__step-header">
                        {state === 'in_progress'
                          ? <Loader2 size={10} className="mock-run-details__step-spinner" />
                          : (
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={10} />
                              {badge.label}
</span>
                          )
                        }
<span className="mock-run-details__step-name">{step.name}</span>
                        {hasScreenshot && (
<span className="mock-run-details__step-has-img">
<ImageIcon size={9} />
</span>
                        )}
</div>
<div className="mock-run-details__step-meta">
                        {step.duration && (
<span className="mock-run-details__duration">{step.duration}</span>
                        )}
                        {step.requestUrl && (
<>
<Globe size={9} />
                            {step.httpMethods && <span className="mock-run-details__step-type">{step.httpMethods}</span>}
<span className="mock-run-details__step-desc">{step.requestUrl}</span>
                            {step.responseStatusCode > 0 && (
<span className={`mock-run-details__code${step.responseStatusCode >= 400 ? ' mock-run-details__code--error' : ''}`}>
                                {step.responseStatusCode}
</span>
                            )}
</>
                        )}
</div>
                      {errors && (
<div className="mock-run-details__step-errors">
                          {errors.map((err, errIdx) => (
<span key={errIdx} className="mock-run-details__step-error">
<AlertTriangle size={9} />
                              {err}
</span>
                          ))}
</div>
                      )}
</div>
</div>
                );
              });
 
              return permLabel ? [permLabel, ...stepRows] : stepRows;
            })}
</div>
 
          {/* Right: live screenshot preview */}
<div className="mock-run-details__stream-preview">
            {activeScreenshot ? (
<img
                src={activeScreenshot}
                alt={activeStep?.name || 'Step screenshot'}
                className="mock-run-details__preview-img"
                onClick={() => setIsExpanded(true)}
              />
            ) : (
<div className="mock-run-details__preview-empty">
<Monitor size={28} />
<span>Screenshots will appear here as steps execute</span>
</div>
            )}
            {activeStep && (
<div className="mock-run-details__preview-caption">
<span className="mock-run-details__step-num">{activeStep.alias}</span>
<span>{activeStep.name}</span>
</div>
            )}
            {selectedStep && (
<button
                className="mock-run-details__view-latest-btn"
                onClick={() => setSelectedStep(null)}
>
<Radio size={10} />
                View Latest
</button>
            )}
</div>
</div>
 
        {/* Expanded screenshot overlay — portal to body so it's above everything */}
        {isExpanded && activeScreenshot && ReactDOM.createPortal(
<div
            className="mock-run-details__img-overlay"
            onClick={() => setIsExpanded(false)}
>
<img src={activeScreenshot} alt="Screenshot" className="mock-run-details__img-expanded" />
</div>,
          document.body
        )}
</>
    );
  };
 
  /* ── Single Permutation ────────────────────────────────────────── */

  /**
   * One permutation needs no table and no disclosure control — there is
   * nothing to choose between and nothing to hide. Name it, state its result,
   * and show the steps.
   */
  const SinglePermutation = ({ perm }) => {
    const badge = getPermBadge(perm);
    const BadgeIcon = badge.icon;
    const failureText = getPermFailureText(perm);

    return (
<div className="mock-run-details__single-perm">
<div className="mock-run-details__single-perm-head">
<Layers size={12} />
<span className="mock-run-details__single-perm-title">
            Permutation {perm.index ?? 1}
</span>
          {perm.description && (
<span className="mock-run-details__single-perm-desc">{perm.description}</span>
          )}
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={11} />
            {badge.label}
</span>
          {perm.duration && (
<span className="mock-run-details__duration">{perm.duration}</span>
          )}
</div>

        {failureText && (
<div className="mock-run-details__perm-error mock-run-details__perm-error--block">
<AlertTriangle size={11} />
            {failureText}
</div>
        )}

        {perm.steps?.length > 0
          ? <PermutationSteps perm={perm} />
          : <div className="mock-run-details__empty">Waiting for steps...</div>}
</div>
    );
  };

  SinglePermutation.propTypes = { perm: PropTypes.object.isRequired };

  /* ── Expandable Permutation Row ────────────────────────────────── */

  const PermutationRow = ({ perm, idx, isExpanded, onToggle }) => {
    const badge = getPermBadge(perm);
    const BadgeIcon = badge.icon;
    const stepCount = perm.numberOfSteps || perm.steps?.length || null;
    // Surface the failure reason only for a genuinely FAILED permutation. A skipped
    // or disabled one has no failing step to report, and its steps report
    // hasPassed: true anyway.
    const failureText = getPermFailureText(perm);
    const hasSteps = perm.steps?.length > 0;

    return (
<>
<tr
          key={`${perm.index}-${idx}`}
          className={`mock-run-details__row ${hasSteps ? 'mock-run-details__row--clickable' : ''}`}
          onClick={hasSteps ? () => onToggle(idx) : undefined}
>
<td>
            {hasSteps && (
              isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
            )}
</td>
<td>{perm.index}</td>
<td>
<div className="mock-run-details__perm-info">
<span className="mock-run-details__perm-name">
                {permutationLabel(perm.index, perm.description)}
</span>
              {failureText && (
<span className="mock-run-details__perm-error">
<AlertTriangle size={9} />
                  {failureText}
</span>
              )}
</div>
</td>
<td>
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={11} />
              {badge.label}
</span>
</td>
<td>
            {stepCount != null && (
<span className="mock-run-details__step-count">{stepCount}</span>
            )}
</td>
<td>
<span className="mock-run-details__duration">{perm.duration || '—'}</span>
</td>
</tr>
 
        {/* Expanded step details */}
        {isExpanded && (
<tr className="mock-run-details__expanded-row">
<td colSpan={6}>
<PermutationSteps perm={perm} />
</td>
</tr>
        )}
</>
    );
  };

  /* ── Permutations Table (Api / Database runs) ──────────────────── */

  /**
   * Permutations open by default and follow the run: the newest one is the one
   * expanded, and it closes only when the next permutation arrives and takes
   * its place. Nothing starts collapsed — a permutation the user has to click
   * to see is a permutation they miss while it streams.
   */
  const PermutationsTable = ({ permutations }) => {
    const [expandedIdx, setExpandedIdx] = useState(permutations.length - 1);
    const countRef = useRef(permutations.length);

    useEffect(() => {
      if (permutations.length > countRef.current) {
        setExpandedIdx(permutations.length - 1);
      }
      countRef.current = permutations.length;
    }, [permutations.length]);

    const handleToggle = useCallback((idx) => {
      setExpandedIdx(prev => prev === idx ? null : idx);
    }, []);

    return (
<table className="mock-run-details__table">
<thead>
<tr>
<th style={{ width: 24 }} />
<th>#</th>
<th>Permutation</th>
<th>Status</th>
<th>Steps</th>
<th>Duration</th>
</tr>
</thead>
<tbody>
          {permutations.map((p, idx) => (
<PermutationRow
              key={`${p.index}-${idx}`}
              perm={p}
              idx={idx}
              isExpanded={expandedIdx === idx}
              onToggle={handleToggle}
            />
          ))}
</tbody>
</table>
    );
  };
 
  const RunDetailsPanel = ({ isOpen, runState }) => {
    const tableWrapRef = useRef(null);
    const userScrolledRef = useRef(false);
    const stepCache = useStepCache();
    const permCache = usePermCache();
 
    // Auto-scroll the table wrapper to bottom as new rows arrive
    // Uses scrollTop instead of scrollIntoView to avoid moving the outer page container
    const permCount = runState?.testResults?.permutations?.length || 0;
    useEffect(() => {
      if (!isOpen || userScrolledRef.current) return;
      const wrap = tableWrapRef.current;
      if (wrap) {
        wrap.scrollTop = wrap.scrollHeight;
      }
    }, [isOpen, permCount]);
 
    // Detect user scroll to pause auto-scroll
    useEffect(() => {
      const wrap = tableWrapRef.current;
      if (!wrap) return;
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = wrap;
        userScrolledRef.current = scrollHeight - scrollTop - clientHeight > 60;
      };
      wrap.addEventListener('scroll', handleScroll, { passive: true });
      return () => wrap.removeEventListener('scroll', handleScroll);
    }, [isOpen]);
 
    // Reset scroll tracking when panel opens, and scroll outer container to bottom
    useEffect(() => {
      if (isOpen) {
        userScrolledRef.current = false;
      }
    }, [isOpen]);
 
    if (!runState?.testResults) return null;
 
    const { testSummary, permutations: rawPermutations = [] } = runState.testResults;
    const { runStatus } = testSummary;

    // Everything that has streamed in is shown, not just what the latest frame
    // held. A frame carries whatever fitted in 128 KB — possibly fewer steps,
    // shorter bodies, or no permutations at all — so the caches are merged back
    // over it: rows the frame omitted keep their place, and a step keeps the
    // fullest body it was ever sent.
    const permutations = (() => {
      const incomingByKey = new Map(
        rawPermutations.map((p, pIdx) => [permKeyOf(p, pIdx), p])
      );
      const keys = [...new Set([...Object.keys(permCache), ...incomingByKey.keys()])];
      const allNumeric = keys.every((key) => /^\d+$/.test(key));
      const ordered = allNumeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;

      return ordered.map((permKey) => {
        const incoming = incomingByKey.get(permKey);
        const shell = mergeRecord(permCache[permKey], incoming ? { ...incoming, steps: undefined } : null);
        return {
          ...shell,
          steps: mergeSteps(stepCache[permKey] || [], incoming?.steps || []),
        };
      });
    })();

    // Counted by status, off the merged list so a frame that arrived with no
    // permutations does not reset the tallies.
    // Note `total` is NOT testSummary.numberOfPermutations — in the
    // stream that field reports how many have executed so far, so it under-reports the
    // grand total (see resolveRunCounts).
    const {
      total: totalPermutations,
      executed: executedPermutations,
      passed: passedPermutations,
      failed: failedPermutations,
      skipped: skippedPermutations,
      disabled: disabledPermutations,
    } = resolveRunCounts(testSummary, permutations);

 
    const isRunning = runStatus === 'IN_PROGRESS' || runStatus === 'RUNNING';
    // Mobile and Web-UI both use the screenshot streaming view
    const hasScreenshotSteps = permutations.some(p =>
      p.steps?.some(s => {
        const t = normaliseStreamStepType(s.stepType);
        return t === 'Web-UI' || t === 'Mobile';
      })
    );
 
    return (
<div className={`mock-run-details${isOpen ? ' mock-run-details--open' : ''}`}>
<div className="mock-run-details__content">
          {/* Header */}
<div className="mock-run-details__header">
<h2 className="mock-run-details__title">Run Details</h2>
<span className="mock-run-details__runid">{runState.runid}</span>
</div>
 
          {/* Meta row — project, type, timestamp */}
          {(runState.projectName || runState.runnableType || runState.timestamp) && (
<div className="mock-run-details__meta">
              {runState.projectName && (
<span className="mock-run-details__meta-tag">
<Layers size={9} />
                  {runState.projectName}
</span>
              )}
              {runState.runnableType && (
<span className="mock-run-details__meta-tag">
                  {runState.runnableType.replace(/_/g, ' ')}
</span>
              )}
              {runState.timestamp && (
<span className="mock-run-details__meta-tag">
<Clock size={9} />
                  {new Date(runState.timestamp).toLocaleTimeString()}
</span>
              )}
</div>
          )}
 
          {/* Permutation stepper — visual pill for each permutation */}
          {totalPermutations > 0 && (
<div className="mock-run-details__perm-stepper">
              {Array.from({ length: totalPermutations }, (_, i) => {
                const perm = permutations[i];
                const permIndex = i + 1;
                const status = getPermutationStatus(perm);
                // Status is the only signal — hasPassed is true for SKIPPED/DISABLED.
                const isCurrent = isRunning && !status && permIndex === executedPermutations + 1;

                let statusClass = 'mock-run-details__perm-pill--pending';
                if (isCurrent) statusClass = 'mock-run-details__perm-pill--running';
                else if (status === PERMUTATION_STATUS.PASSED) statusClass = 'mock-run-details__perm-pill--passed';
                else if (status === PERMUTATION_STATUS.FAILED) statusClass = 'mock-run-details__perm-pill--failed';
                else if (status === PERMUTATION_STATUS.SKIPPED) statusClass = 'mock-run-details__perm-pill--skipped';
                else if (status === PERMUTATION_STATUS.DISABLED) statusClass = 'mock-run-details__perm-pill--disabled';

                return (
<div
                    key={i}
                    className={`mock-run-details__perm-pill ${statusClass}`}
                    title={status ? (STATUS_BADGE[status]?.label || status) : 'Pending'}
>
<span className="mock-run-details__perm-pill-num">{permIndex}</span>
                    {isCurrent && <Loader2 size={10} className="mock-run-details__perm-pill-spinner" />}
                    {!isCurrent && status === PERMUTATION_STATUS.PASSED && <CircleCheck size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.FAILED && <CircleX size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.SKIPPED && <MinusCircle size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.DISABLED && <Ban size={10} />}
</div>
                );
              })}
</div>
          )}

          {/* Summary counts — skipped / disabled shown only when present so a
              straightforward run keeps the compact four-stat row */}
<div className="mock-run-details__summary">
<div className="mock-run-details__stat">
<span className="mock-run-details__stat-label">Total</span>
<span className="mock-run-details__stat-value">{totalPermutations}</span>
</div>
<div className="mock-run-details__stat">
<span className="mock-run-details__stat-label">Executed</span>
<span className="mock-run-details__stat-value">{executedPermutations}</span>
</div>
<div className="mock-run-details__stat mock-run-details__stat--passed">
<span className="mock-run-details__stat-label">Passed</span>
<span className="mock-run-details__stat-value">{passedPermutations}</span>
</div>
<div className="mock-run-details__stat mock-run-details__stat--failed">
<span className="mock-run-details__stat-label">Failed</span>
<span className="mock-run-details__stat-value">{failedPermutations}</span>
</div>
              {skippedPermutations > 0 && (
<div className="mock-run-details__stat mock-run-details__stat--skipped" title="Every step in these permutations was disabled, so nothing ran">
<span className="mock-run-details__stat-label">Skipped</span>
<span className="mock-run-details__stat-value">{skippedPermutations}</span>
</div>
              )}
              {disabledPermutations > 0 && (
<div className="mock-run-details__stat mock-run-details__stat--disabled" title="Turned off in the data source">
<span className="mock-run-details__stat-label">Disabled</span>
<span className="mock-run-details__stat-value">{disabledPermutations}</span>
</div>
              )}
</div>
 
          {/* Results — Web-UI steps view OR permutations table */}
<div className="mock-run-details__table-wrap" ref={tableWrapRef}>
            {permutations.length === 0 ? (
<div className="mock-run-details__empty">
                {isRunning ? 'Waiting for results...' : 'No permutations recorded.'}
</div>
            ) : hasScreenshotSteps ? (
<WebUiStepsView permutations={permutations} />
            ) : permutations.length === 1 ? (
<SinglePermutation perm={permutations[0]} />
            ) : (
<PermutationsTable permutations={permutations} />
            )}
</div>
</div>
</div>
    );
  };
 
  RunDetailsPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    runState: PropTypes.object,
  };                                                                                                                                                                                                                                     
  RunDetailsPanel.defaultProps = {                                                                                                                                                                                                       
    runState: null,                                         
  };
 
  export default RunDetailsPanel;